import { useEffect, useState } from "react";
import { FileCode2, Plus, Send } from "lucide-react";
import { toast } from "sonner";
import { Card, EmptyState, ErrorBanner, Notice, PageHeader, Spinner, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { AttachmentPicker, InsertImage, SpamCheck } from "../../components/TemplateTools";

const BLANK = { name: "", subject: "", html: "<p>Hello {{name}},</p>\n<p>…</p>", text: "Hello {{name}}," };

function Preview({ tid, variables }) {
  const { t } = useT();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!tid) return;
    const h = setTimeout(() => api.post(`/app/templates/${tid}/preview`, { variables }).then((r) => { setP(r.data); setError(null); }).catch((e) => setError(errMsg(e))), 250);
    return () => clearTimeout(h);
  }, [tid, variables]);
  if (!tid) return <p className="text-sm text-zinc-500">{t("tpl.savefirst")}</p>;
  return (
    <div className="space-y-3" data-testid="template-preview">
      <ErrorBanner error={error} />
      {p && <>
        <div className="text-sm"><span className="text-zinc-500">{t("tpl.subject")}: </span><span className="text-zinc-200" data-testid="template-preview-subject">{p.subject}</span></div>
        {p.missing.length > 0 && <p className="text-xs text-amber-300">Missing variables render empty: {p.missing.join(", ")}</p>}
        {/* sandbox="" blocks scripts, forms and same-origin access so template HTML cannot affect the admin UI */}
        <iframe title="Template preview" sandbox="" srcDoc={p.html || `<pre>${(p.text || "").replace(/</g, "&lt;")}</pre>`} className="h-80 w-full rounded-lg border border-white/10 bg-white" data-testid="template-preview-frame" />
      </>}
    </div>
  );
}

function TestSend({ tid, variables, sending }) {
  const { t } = useT();
  const [form, setForm] = useState({ from: "", to: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const disabled = !sending?.can_send;
  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`This sends a REAL email to ${form.to} through the live provider. Continue?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/app/templates/${tid}/test-send`, { ...form, variables, attachment_ids: files }); toast.success(`Queued as ${data.id}`); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="template-test-send-form">
      <div className="text-sm font-medium text-white">{t("tpl.testsend")}</div>
      {disabled && <Notice tone="warn" testId="test-send-disabled">{sending?.blockers?.join(" · ") || "Sending is unavailable"}</Notice>}
      <Input placeholder="from: you@verified-domain.com" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} required disabled={disabled} aria-label="From" data-testid="test-send-from" />
      <Input placeholder="to: allowed recipient" type="email" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} required disabled={disabled} aria-label="To" data-testid="test-send-to" />
      {!disabled && <AttachmentPicker value={files} onChange={setFiles} />}
      <Button type="submit" size="sm" disabled={disabled || busy || !tid} data-testid="test-send-submit"><Send className="h-3.5 w-3.5" />{t("tpl.sendtest")}</Button>
    </form>
  );
}

function Editor({ tpl, onSaved, onDeleted, canEdit, sending }) {
  const { t } = useT();
  const [form, setForm] = useState(tpl ? { name: tpl.name, subject: tpl.subject, html: tpl.html, text: tpl.text } : BLANK);
  const [vars, setVars] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const { data } = tpl ? await api.put(`/app/templates/${tpl.id}`, form) : await api.post("/app/templates", form);
      toast.success(t("tpl.saved")); onSaved(data);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/app/templates/${tpl.id}`); toast.success("Template deleted"); onDeleted(); } catch (err) { toast.error(errMsg(err)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card testId="template-editor">
        <form onSubmit={save} className="space-y-4">
          <ErrorBanner error={error} testId="template-error" />
          <div className="space-y-1.5"><Label htmlFor="tn">{t("tpl.name")}</Label><Input id="tn" required value={form.name} onChange={set("name")} disabled={!canEdit} data-testid="template-name" /></div>
          <div className="space-y-1.5"><Label htmlFor="ts">{t("tpl.subject")}</Label><Input id="ts" required value={form.subject} onChange={set("subject")} disabled={!canEdit} data-testid="template-subject" /></div>
          <div className="space-y-1.5"><div className="flex items-center justify-between"><Label htmlFor="th">HTML</Label>{canEdit && <InsertImage onInsert={(tag) => setForm((f) => ({ ...f, html: `${f.html}\n${tag}` }))} />}</div><Textarea id="th" rows={10} className="font-mono text-xs" value={form.html} onChange={set("html")} disabled={!canEdit} data-testid="template-html" /></div>
          <div className="space-y-1.5"><Label htmlFor="tt">{t("tpl.text")}</Label><Textarea id="tt" rows={4} className="font-mono text-xs" value={form.text} onChange={set("text")} disabled={!canEdit} data-testid="template-text" /></div>
          <p className="text-xs text-zinc-500">{t("tpl.use")} <code className="text-purple-300">{"{{variable}}"}</code>. {t("tpl.vars")}</p>
          {canEdit && <div className="flex gap-2"><Button type="submit" disabled={busy} data-testid="template-save">{t("common.save")}</Button>
            {tpl && <Button type="button" variant="ghost" onClick={del} className="text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="template-delete">{t("common.delete")}</Button>}</div>}
        </form>
      </Card>
      <div className="space-y-6">
        <Card>
          {tpl?.variables?.length > 0 && <div className="mb-4 grid gap-2 sm:grid-cols-2">{tpl.variables.map((v) => (
            <Input key={v} placeholder={v} value={vars[v] || ""} onChange={(e) => setVars({ ...vars, [v]: e.target.value })} aria-label={`Variable ${v}`} data-testid={`template-var-${v}`} />))}</div>}
          <Preview tid={tpl?.id} variables={vars} />
        </Card>
        {tpl && <SpamCheck tpl={tpl} canEdit={canEdit} onApplied={onSaved} />}
        {tpl && canEdit && <Card><TestSend tid={tpl.id} variables={vars} sending={sending} /></Card>}
      </div>
    </div>
  );
}

export default function Templates() {
  const { t } = useT();
  const { can } = useAuth();
  const [sel, setSel] = useState(null);
  const list = useLoad(() => api.get("/app/templates").then((r) => r.data));
  const sending = useLoad(() => api.get("/app/sending-status").then((r) => r.data));
  const current = useLoad(() => (sel && sel !== "new" ? api.get(`/app/templates/${sel}`).then((r) => r.data) : Promise.resolve(null)), [sel]);
  return (
    <div>
      <PageHeader title={t("nav.templates")} description={t("desc.templates")}
        actions={can("developer") && <Button onClick={() => setSel("new")} data-testid="template-new-button"><Plus className="h-4 w-4" />{t("tpl.new")}</Button>} />
      <ErrorBanner error={list.error} />
      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <div className="space-y-1" data-testid="template-list">
          {list.loading && <Spinner />}
          {!list.loading && list.data?.map((tp) => (
            <button key={tp.id} onClick={() => setSel(tp.id)} data-testid={`template-item-${tp.id}`}
              className={`block w-full truncate rounded-md px-3 py-2 text-left text-sm transition-colors ${sel === tp.id ? "bg-purple-500/10 text-white" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}>{tp.name}</button>
          ))}
          {!list.loading && !list.data?.length && <p className="px-3 text-sm text-zinc-500">{t("tpl.none")}</p>}
        </div>
        <div>
          {!sel && <EmptyState icon={FileCode2} title={t("tpl.select")} testId="template-empty" />}
          {sel && sel !== "new" && current.loading && <Spinner />}
          {sel && (sel === "new" || !current.loading) && <Editor key={sel + (current.data?.updated_at || "")} tpl={sel === "new" ? null : current.data} canEdit={can("developer")} sending={sending.data}
                onSaved={(d) => { list.reload(); if (d.id === sel) current.reload(); else setSel(d.id); }} onDeleted={() => { setSel(null); list.reload(); }} />}
        </div>
      </div>
    </div>
  );
}
